import {
	Box,
	Stack,
	Typography,
	Divider,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableFooter,
	Tabs,
	Tab,
	Button,
	IconButton,
	Collapse,
	Skeleton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Checkbox,
	FormGroup,
	FormControlLabel,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { Fragment, useState } from "react";
import dayjs from "dayjs";
import { useResponsive } from "../../utils/mobileresponsive";
import { generateButtonSx } from "../../utils/elementor-style-override";
import useFetchReports from "./hooks/useFetchReports";
import { MonthPicker } from "../../components/ui/MonthPicker";
import exportReportsExcel, { EXPORT_SHEETS, EXPORT_SHEET_KEYS } from "./utils/ExportReportExcel";

const LEVEL_CONFIG = {
	L1: { color: "#006064", bg: "#E0F7FA", accent: "#00ACC1", rate: 0.5 },
	L2: { color: "#1565C0", bg: "#E3F2FD", accent: "#1976D2", rate: 0.3 },
	L3: { color: "#4527A0", bg: "#EDE7F6", accent: "#7E57C2", rate: 0.2 },
};

const PAYMENT_STATUS_CONFIG = {
	Paid: { color: "#2E7D32", bg: "#E8F5E9" },
	Pending: { color: "#EF6C00", bg: "#FFF3E0" },
	Processing: { color: "#1565C0", bg: "#E3F2FD" },
};

// ─── Period helpers ────────────────────────────────────────────────────────
// Backend period format is "YYYY-MM" (e.g. "2026-07"). MonthPicker works with
// dayjs objects, so we only convert at the edges — state stays a string.

const getCurrentPeriod = () => dayjs().format("YYYY-MM");
const periodToDayjs = (period) => (period ? dayjs(`${period}-01`) : dayjs());

// ─── Shared table styles ─────────────────────────────────────────────────────

const TH = ({ children, align = "left" }) => (
	<TableCell
		align={align}
		sx={{
			fontSize: 11,
			fontWeight: 700,
			color: "#546E7A",
			py: 1.25,
			bgcolor: "#F5F7FA",
			borderBottom: "2px solid #E0E7EF",
			whiteSpace: "nowrap",
		}}
	>
		{children}
	</TableCell>
);

const TD = ({ children, align = "left", sx = {} }) => (
	<TableCell align={align} sx={{ fontSize: 12, py: 1.1, color: "text.primary", ...sx }}>
		{children}
	</TableCell>
);

const TFoot = ({ children, align = "left" }) => (
	<TableCell align={align} sx={{ fontSize: 12, fontWeight: 700, py: 1.25, bgcolor: "#F5F7FA", borderTop: "2px solid #E0E7EF", color: "#1D54A2" }}>
		{children}
	</TableCell>
);

const LevelChip = ({ level }) => {
	const cfg = LEVEL_CONFIG[level];
	return (
		<Chip
			label={level}
			size="small"
			sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: cfg.bg, color: cfg.color, "& .MuiChip-label": { px: 0.75 } }}
		/>
	);
};

const StatusChip = ({ label, color, bg }) => (
	<Chip label={label} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: bg, color, "& .MuiChip-label": { px: 0.75 } }} />
);

const PaymentStatusChip = ({ status }) => {
	const cfg = PAYMENT_STATUS_CONFIG[status] || PAYMENT_STATUS_CONFIG.Pending;
	return <StatusChip label={status} color={cfg.color} bg={cfg.bg} />;
};

const LicensingChip = ({ status }) =>
	status === "Active" ? <StatusChip label="Active" color="#2E7D32" bg="#E8F5E9" /> : <StatusChip label="Inactive" color="#C62828" bg="#FFEBEE" />;

// ─── Skeleton rows (shown while useFetchReports is loading) ──────────────────

const SkeletonRow = ({ columns }) => (
	<TableRow>
		{columns.map((col, i) => (
			<TD key={i} align={col.align}>
				<Skeleton variant="text" width={col.w} sx={{ display: "inline-block" }} />
			</TD>
		))}
	</TableRow>
);

const SkeletonRows = ({ columns, rows = 5 }) => Array.from({ length: rows }).map((_, i) => <SkeletonRow key={i} columns={columns} />);

// ─── Empty state (shown when not loading and data is empty) ─────────────────

const EmptyState = ({ colSpan, title = "No records found", subtitle }) => (
	<TableRow>
		<TableCell colSpan={colSpan} sx={{ border: 0, py: 6 }}>
			<Stack alignItems="center" justifyContent="center" spacing={1}>
				<Box
					sx={{
						width: 48,
						height: 48,
						borderRadius: "50%",
						bgcolor: "#F5F7FA",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<InboxOutlinedIcon sx={{ fontSize: 24, color: "#B0BEC5" }} />
				</Box>
				<Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>{title}</Typography>
				{subtitle && <Typography sx={{ fontSize: 11.5, color: "text.disabled", textAlign: "center", maxWidth: 320 }}>{subtitle}</Typography>}
			</Stack>
		</TableCell>
	</TableRow>
);

// ─── Downline detail (shown on "View Downline") ───────────────────────────────

const DownlineLevelBlock = ({ levelKey, agents }) => {
	if (!agents || agents.length === 0) return null;
	const cfg = LEVEL_CONFIG[levelKey];
	const subtotal = agents.reduce((s, a) => s + a.earned, 0);

	return (
		<Box mb={1.5} sx={{ "&:last-child": { mb: 0 } }}>
			<Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
				<LevelChip level={levelKey} />
				<Typography fontSize={11} color="text.secondary">
					{agents.length} agent{agents.length !== 1 ? "s" : ""} · ${cfg.rate.toFixed(2)} / member
				</Typography>
			</Stack>
			<TableContainer sx={{ border: "1px solid #E8EDF2", borderRadius: 1, overflow: "hidden" }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TH>Agent Name</TH>
							<TH align="right">Members</TH>
							<TH align="right">Rate / Member</TH>
							<TH align="right">Override Earned</TH>
						</TableRow>
					</TableHead>
					<TableBody>
						{/* Keyed by id, not name: every placeholder carries the same name, so
						    keying by it would collide whenever a level holds more than one. */}
						{agents.map((a) => (
							<TableRow key={a.id} sx={{ "&:last-child td": { border: 0 } }}>
								<TD
									sx={{
										fontWeight: 600,
										color: a.isPlaceholder ? "text.disabled" : "inherit",
										fontStyle: a.isPlaceholder ? "italic" : "normal",
									}}
								>
									{a.name}
								</TD>
								<TD align="right">{a.isPlaceholder ? "—" : a.members.toLocaleString()}</TD>
								<TD align="right">${a.rate.toFixed(2)}</TD>
								<TD align="right" sx={{ fontWeight: 700, color: a.isPlaceholder ? "text.disabled" : cfg.accent }}>
									{a.isPlaceholder ? "—" : `$${a.earned.toFixed(2)}`}
								</TD>
							</TableRow>
						))}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TFoot sx={{ py: 0.75 }}>Subtotal</TFoot>
							<TFoot></TFoot>
							<TFoot></TFoot>
							<TFoot align="right" sx={{ py: 0.75 }}>
								${subtotal.toFixed(2)}
							</TFoot>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</Box>
	);
};

const DownlineDetail = ({ agent }) => {
	const hasAny = ["L1", "L2", "L3"].some((lvl) => agent.downline[lvl]?.length);

	return (
		<Box sx={{ bgcolor: "#FAFBFC", p: 2, borderTop: "1px solid #E8EDF2" }}>
			{hasAny ? (
				["L1", "L2", "L3"].map((lvl) => <DownlineLevelBlock key={lvl} levelKey={lvl} agents={agent.downline[lvl]} />)
			) : (
				<Typography fontSize={12} color="text.disabled" fontStyle="italic">
					No downline agents for {agent.name}.
				</Typography>
			)}
		</Box>
	);
};

// ─── Report sections ──────────────────────────────────────────────────────────

const OverrideReport = ({ data = [], loading = false }) => {
	const [expandedIds, setExpandedIds] = useState(new Set());

	const toggleExpanded = (id) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const isEmpty = !loading && data.length === 0;
	const totalOverride = data.reduce((sum, agent) => sum + agent.totalOverride, 0);

	return (
		<>
			<Box
				sx={{
					mb: 0,
					p: 1.5,
					bgcolor: "#FFF8E1",
					border: "1px solid #FFE082",
					borderRadius: 1,
				}}
			>
				<Typography fontSize={11.5} color="#8D6E00" sx={{ lineHeight: 1.6 }}>
					<strong>Qualification criteria:</strong> Agents qualify for override earnings when they have{" "}
					<strong>≥ 25 personal members</strong> or <strong>≥ 100 network members</strong>, AND hold an{" "}
					<strong>Active Insurance License</strong>, AND are in <strong>Good Standing</strong> with Ally Health Group.
				</Typography>
			</Box>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TH>Rank</TH>
							<TH>Agent Name</TH>
							<TH align="right">Total Override Earnings</TH>
							<TH align="center">Payment Status</TH>
							<TH align="center">Override Status</TH>
							<TH align="center">Downline</TH>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<SkeletonRows
								columns={[
									{ w: 16 },
									{ w: 120 },
									{ w: 70, align: "right" },
									{ w: 64, align: "center" },
									{ w: 80, align: "center" },
									{ w: 90, align: "center" },
								]}
							/>
						) : isEmpty ? (
							<EmptyState colSpan={6} title="No override data" subtitle="There's no override activity to show for this period yet." />
						) : (
							data.map((agent, i) => {
								const isExpanded = expandedIds.has(agent.id);
								return (
									<Fragment key={agent.id}>
										<TableRow sx={{ "&:hover": { bgcolor: "#FAFBFC" }, "& td": { border: isExpanded ? 0 : undefined } }}>
											<TD sx={{ color: "text.disabled" }}>{i + 1}</TD>
											<TD sx={{ fontWeight: 600 }}>{agent.name}</TD>
											<TD align="right" sx={{ fontWeight: 700, color: "#1D54A2" }}>
												${agent.totalOverride.toFixed(2)}
											</TD>
											<TD align="center">
												<PaymentStatusChip status={agent.paymentStatus} />
											</TD>
											<TD align="center">
												{agent.qualified ? (
													<StatusChip label="Eligible" color="#2E7D32" bg="#E8F5E9" />
												) : (
													<StatusChip label="Not Eligible" color="#C62828" bg="#FFEBEE" />
												)}
											</TD>
											<TD align="center">
												<Button
													size="small"
													variant="outlined"
													onClick={() => toggleExpanded(agent.id)}
													endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
													sx={{
														fontSize: 11,
														textTransform: "none",
														color: "#1D54A2",
														fontWeight: 600,
														backgroundColor: "transparent !important",
														...generateButtonSx(
															{ bg: "#F4F4F5 !important", text: "#1D54A2" },
															{ bg: "#F4F4F5 !important", text: "#1D54A2" }
														),
													}}
												>
													{isExpanded ? "Hide Downline" : "View Downline"}
												</Button>
											</TD>
										</TableRow>
										<TableRow>
											<TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
												<Collapse in={isExpanded} timeout="auto" unmountOnExit>
													<DownlineDetail agent={agent} />
												</Collapse>
											</TableCell>
										</TableRow>
									</Fragment>
								);
							})
						)}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TFoot></TFoot>
							<TFoot>Total</TFoot>
							<TFoot align="right">
								{loading ? <Skeleton variant="text" width={60} sx={{ ml: "auto" }} /> : `$${totalOverride.toFixed(2)}`}
							</TFoot>
							<TFoot></TFoot>
							<TFoot></TFoot>
							<TFoot></TFoot>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</>
	);
};

const AgentPerformanceReport = ({ data = [], loading = false }) => {
	const rows = data;
	const isEmpty = !loading && rows.length === 0;
	const totalPolicyCount = rows.reduce((s, r) => s + r.policyCount, 0);

	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TH>Rank</TH>
						<TH>Agent Name</TH>
						<TH align="right">Personal Active Members</TH>
						<TH align="right">Network Members</TH>
						<TH align="right">Policy Count</TH>
						<TH align="center">Licensing</TH>
					</TableRow>
				</TableHead>
				<TableBody>
					{loading ? (
						<SkeletonRows
							columns={[
								{ w: 16 },
								{ w: 120 },
								{ w: 60, align: "right" },
								{ w: 60, align: "right" },
								{ w: 60, align: "right" },
								{ w: 64, align: "center" },
							]}
						/>
					) : isEmpty ? (
						<EmptyState colSpan={6} title="No agent performance data" subtitle="There's nothing to show for this period yet." />
					) : (
						rows.map((row, i) => (
							<TableRow key={row.name} sx={{ "&:hover": { bgcolor: "#FAFBFC" }, "&:last-child td": { border: 0 } }}>
								<TD sx={{ color: "text.disabled" }}>{i + 1}</TD>
								<TD sx={{ fontWeight: 600 }}>{row.name}</TD>
								<TD align="right">{row.personal.toLocaleString()}</TD>
								<TD align="right">{row.network.toLocaleString()}</TD>
								<TD align="right" sx={{ fontWeight: 700, color: "#1D54A2" }}>
									{row.policyCount.toLocaleString()}
								</TD>
								<TD align="center">
									<LicensingChip status={row.licensing} />
								</TD>
							</TableRow>
						))
					)}
				</TableBody>
				<TableFooter>
					<TableRow>
						<TFoot></TFoot>
						<TFoot>Total</TFoot>
						<TFoot></TFoot>
						<TFoot></TFoot>
						<TFoot align="right">
							{loading ? <Skeleton variant="text" width={40} sx={{ ml: "auto" }} /> : totalPolicyCount.toLocaleString()}
						</TFoot>
						<TFoot></TFoot>
					</TableRow>
				</TableFooter>
			</Table>
		</TableContainer>
	);
};

const LeadershipReport = ({ data = [], loading = false }) => {
	const isEmpty = !loading && data.length === 0;
	const totalPaid = data.filter((r) => r.status === "Eligible").reduce((sum, r) => sum + Number(r.bonus.replace("$", "")), 0);

	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TH>#</TH>
						<TH>Agent Name</TH>
						<TH>Milestone</TH>
						<TH align="right">Current Members</TH>
						<TH align="right">Target</TH>
						<TH align="right">Bonus Amount</TH>
						<TH align="center">Achieved Date</TH>
						<TH align="center">Status</TH>
					</TableRow>
				</TableHead>
				<TableBody>
					{loading ? (
						<SkeletonRows
							columns={[
								{ w: 16 },
								{ w: 120 },
								{ w: 90 },
								{ w: 60, align: "right" },
								{ w: 60, align: "right" },
								{ w: 60, align: "right" },
								{ w: 70, align: "center" },
								{ w: 80, align: "center" },
							]}
						/>
					) : isEmpty ? (
						<EmptyState colSpan={8} title="No leadership bonuses" subtitle="No milestones to show for this period yet." />
					) : (
						data.map((row, i) => {
							const pct = Math.min(100, Math.round((row.current / row.target) * 100));
							const statusColor =
								row.status === "Eligible"
									? { color: "#2E7D32", bg: "#E8F5E9" }
									: row.status === "In Progress"
										? { color: "#1565C0", bg: "#E3F2FD" }
										: { color: "#78909C", bg: "#ECEFF1" };
							return (
								<TableRow
									key={`${row.agent}-${row.milestone}`}
									sx={{ "&:hover": { bgcolor: "#FAFBFC" }, "&:last-child td": { border: 0 } }}
								>
									<TD sx={{ color: "text.disabled" }}>{i + 1}</TD>
									<TD sx={{ fontWeight: 600 }}>{row.agent}</TD>
									<TD>{row.milestone}</TD>
									<TD align="right">{row.current.toLocaleString()}</TD>
									<TD align="right">{row.target.toLocaleString()}</TD>
									<TD align="right" sx={{ fontWeight: 700, color: "#1D54A2" }}>
										{row.bonus}
									</TD>
									<TD align="center" sx={{ color: row.achievedDate ? "text.primary" : "text.disabled" }}>
										{row.achievedDate ?? "—"}
									</TD>
									<TD align="center">
										<Stack alignItems="center" spacing={0.5}>
											<StatusChip label={row.status} color={statusColor.color} bg={statusColor.bg} />
											<Typography fontSize={10} color="text.disabled">
												{pct}% reached
											</Typography>
										</Stack>
									</TD>
								</TableRow>
							);
						})
					)}
				</TableBody>
				<TableFooter>
					<TableRow>
						<TFoot></TFoot>
						<TFoot>Total Eligible Bonuses</TFoot>
						<TFoot></TFoot>
						<TFoot></TFoot>
						<TFoot></TFoot>
						<TFoot align="right">
							{loading ? <Skeleton variant="text" width={50} sx={{ ml: "auto" }} /> : `$${totalPaid.toLocaleString()}`}
						</TFoot>
						<TFoot></TFoot>
						<TFoot align="center">
							{loading ? (
								<Skeleton variant="text" width={60} sx={{ mx: "auto" }} />
							) : (
								`${data.filter((r) => r.status === "Eligible").length} eligible`
							)}
						</TFoot>
					</TableRow>
				</TableFooter>
			</Table>
		</TableContainer>
	);
};

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const NET_DIRECTION_CONFIG = {
	payout: { label: "Payout to Agent", color: "#2E7D32", bg: "#E8F5E9" },
	payable: { label: "Agents Need to Pay", color: "#B45309", bg: "#FFF7ED" },
	settled: { label: "Settled", color: "#546E7A", bg: "#ECEFF1" },
};

const NetMonthlyCell = ({ amount, direction }) => {
	const cfg = NET_DIRECTION_CONFIG[direction] ?? NET_DIRECTION_CONFIG.settled;
	return (
		<Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75}>
			<Typography sx={{ fontSize: 12, fontWeight: 700, color: cfg.color }}>{money(Math.abs(amount))}</Typography>
			<StatusChip label={cfg.label} color={cfg.color} bg={cfg.bg} />
		</Stack>
	);
};

const MonthlyTotalsReport = ({ data = [], loading = false }) => {
	const isEmpty = !loading && data.length === 0;

	const totalOverride = data.reduce((s, r) => s + r.overrideEarnings, 0);
	const totalResidual = data.reduce((s, r) => s + r.residualIncome, 0);
	const totalFees = data.reduce((s, r) => s + r.serviceFees, 0);
	const totalPayable = data.reduce((s, r) => s + r.payable, 0);
	const totalPayout = data.reduce((s, r) => s + r.payout, 0);

	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TH>#</TH>
						<TH>Agent Name</TH>
						<TH align="right">Override Earnings</TH>
						<TH align="right">Network Residual Income</TH>
						<TH align="right">Personal Book Service Fees</TH>
						<TH align="right">Net Monthly</TH>
					</TableRow>
				</TableHead>
				<TableBody>
					{loading ? (
						<SkeletonRows
							columns={[{ w: 16 }, { w: 130 }, { w: 80, align: "right" }, { w: 90, align: "right" }, { w: 90, align: "right" }, { w: 110, align: "right" }]}
						/>
					) : isEmpty ? (
						<EmptyState colSpan={6} title="No monthly totals" subtitle="Nothing has been computed for this period yet." />
					) : (
						data.map((row, i) => (
							<TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#FAFBFC" }, "&:last-child td": { border: 0 } }}>
								<TD sx={{ color: "text.disabled" }}>{i + 1}</TD>
								{/* No opted-out marker needed: agents opted out for this period
								    are excluded from the report entirely, server side. */}
								<TD sx={{ fontWeight: 600 }}>{row.name}</TD>
								<TD align="right">{money(row.overrideEarnings)}</TD>
								<TD align="right" sx={{ fontWeight: 600 }}>
									{money(row.residualIncome)}
								</TD>
								<TD align="right">{money(row.serviceFees)}</TD>
								<TD align="right">
									<NetMonthlyCell amount={row.netMonthly} direction={row.netDirection} />
								</TD>
							</TableRow>
						))
					)}
				</TableBody>
				<TableFooter>
					<TableRow>
						<TFoot></TFoot>
						<TFoot>Total</TFoot>
						<TFoot align="right">{loading ? <Skeleton variant="text" width={60} sx={{ ml: "auto" }} /> : money(totalOverride)}</TFoot>
						<TFoot align="right">{loading ? <Skeleton variant="text" width={60} sx={{ ml: "auto" }} /> : money(totalResidual)}</TFoot>
						<TFoot align="right">{loading ? <Skeleton variant="text" width={60} sx={{ ml: "auto" }} /> : money(totalFees)}</TFoot>
						<TFoot align="right">
							{loading ? (
								<Skeleton variant="text" width={90} sx={{ ml: "auto" }} />
							) : (
								<Stack direction="row" justifyContent="flex-end" spacing={1.5}>
									<span style={{ color: "#B45309" }}>{money(totalPayable)} to collect</span>
									<span style={{ color: "#2E7D32" }}>{money(totalPayout)} to pay out</span>
								</Stack>
							)}
						</TFoot>
					</TableRow>
				</TableFooter>
			</Table>
		</TableContainer>
	);
};

// ─── Formatters ────────────────────────────────────────────────────────
const formatMonthlyTotals = (monthlyTotals = []) =>
	monthlyTotals.map((row) => ({
		id: row.agent_id,
		name: row.agent_name,
		overrideEarnings: Number(row.override_earnings),
		leadershipEarnings: Number(row.leadership_earnings),
		residualIncome: Number(row.network_residual_income),
		serviceFees: Number(row.monthly_service_revenue),
		payable: Number(row.net_monthly_payable),
		payout: Number(row.net_monthly_payout),
		netMonthly: Number(row.net_monthly),
		netDirection: row.net_direction,
	}));

const formatOverrideAgents = (overrideReport = []) =>
	overrideReport.map((agent) => ({
		id: agent.agent_id,
		name: agent.agent_name,
		totalOverride: Number(agent.total_override),
		paymentStatus: agent.payout_status,
		qualified: agent.override_status === "Eligible",
		downline: agent.downline.reduce(
			(acc, item) => {
				if (!acc[item.level]) {
					acc[item.level] = [];
				}

				acc[item.level].push({
					id: item.agent_id,
					name: item.agent_name,
					members: item.active_members,
					rate: Number(item.rate),
					earned: Number(item.override_earned),
					// An opted-out downline the backend kept unnamed so the opted-in
					// agents beneath them keep their place in this breakdown.
					isPlaceholder: item.is_placeholder === true,
				});

				return acc;
			},
			{
				L1: [],
				L2: [],
				L3: [],
			}
		),
	}));

const formatPerformanceRows = (agentPerformance = []) =>
	agentPerformance.map((agent) => ({
		name: agent.agent_name,
		personal: Number(agent.personal_members),
		network: Number(agent.network_members),
		policyCount: Number(agent.policy_count),
		licensing: agent.licensing,
	}));

const formatLeadershipRows = (leadershipReport = []) =>
	leadershipReport.map((item) => ({
		agent: item.agent_name,
		milestone: `${Number(item.target).toLocaleString()} Members`,
		current: Number(item.progress),
		target: Number(item.target),
		bonus: `$${Number(item.reward).toFixed(2)}`,
		achievedDate: item.achieved_date ? dayjs(item.achieved_date).format("MMM D, YYYY") : null,
		status: item.status !== "pending" ? "Eligible" : "In Progress",
	}));

// ─── Main page ────────────────────────────────────────────────────────────────

const TABS = [
	{ label: "Override Report", value: 0 },
	{ label: "Agent Performance", value: 1 },
	{ label: "Leadership Bonuses", value: 2 },
	{ label: "Total Computations", value: 3 },
];

export default function Reports() {
	const [tab, setTab] = useState(0);
	const [exportOpen, setExportOpen] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [selectedSheets, setSelectedSheets] = useState(EXPORT_SHEET_KEYS);
	const { isMobile } = useResponsive();
	const [period, setPeriod] = useState(getCurrentPeriod());

	const { reports, loading } = useFetchReports(period);

	const overrideAgents = formatOverrideAgents(reports?.overrideReport);
	const performanceRows = formatPerformanceRows(reports?.agentPerformance);
	const leadershipRows = formatLeadershipRows(reports?.leadershipReport);
	const monthlyTotalsRows = formatMonthlyTotals(reports?.monthlyTotals);

	const handlePeriodChange = (newValue) => {
		if (!newValue) return;
		setPeriod(newValue.format("YYYY-MM"));
	};

	const toggleSheet = (key) => setSelectedSheets((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

	const allSheetsSelected = selectedSheets.length === EXPORT_SHEETS.length;

	const toggleAllSheets = () => setSelectedSheets(allSheetsSelected ? [] : EXPORT_SHEET_KEYS);

	const handleExport = async () => {
		if (selectedSheets.length === 0) return;

		setExporting(true);
		try {
			await exportReportsExcel({
				period,
				generatedAt: reports?.generated_at,
				overrideAgents,
				performanceRows,
				leadershipRows,
				monthlyTotalsRows,
				// Keep the workbook in tab order regardless of click order.
				sheets: EXPORT_SHEET_KEYS.filter((k) => selectedSheets.includes(k)),
			});
			setExportOpen(false);
		} finally {
			setExporting(false);
		}
	};

	return (
		<Box p={4}>
			{/* Page header */}
			<Stack direction="row" alignItems="center !important" justifyContent="space-between !important" px={3} mb={2}>
				<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
					REPORTS
				</Typography>
				<Button
					startIcon={<SystemUpdateAltIcon />}
					variant="contained"
					onClick={() => setExportOpen(true)}
					disabled={loading}
					sx={{
						px: 3.5,
						gap: 1,
						...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
					}}
				>
					{isMobile ? "" : "Download Reports Table"}
				</Button>
			</Stack>
			<Divider />

			{/* Filter bar */}
			<Stack direction="row" alignItems="center" justifyContent={"space-between"} spacing={2} px={3} my={3} flexWrap="wrap">
				<Typography fontSize={12} color="text.disabled">
					Period: <strong style={{ color: "#1D54A2" }}>{periodToDayjs(period).format("MMMM YYYY")}</strong>
				</Typography>
				<Stack direction={"row"} alignItems={"center !important"} gap={1}>
					<Typography fontSize={12} color="text.disabled" sx={{ ml: "auto !important" }}>
						Select Period:
					</Typography>
					<MonthPicker value={periodToDayjs(period)} onChange={handlePeriodChange} />
				</Stack>
			</Stack>

			{/* Tabs */}
			<Box px={3}>
				<Tabs
					value={tab}
					onChange={(_, v) => setTab(v)}
					sx={{
						borderBottom: "2px solid #E0E7EF",
						mb: 0,
						"& .MuiTab-root": {
							fontSize: 13,
							textTransform: "none",
							fontWeight: 500,
							color: "#ffffff",
							minHeight: 40,
							px: 2,
							borderRadius: "6px 6px 0 0",
							transition: "background-color 0.15s, color 0.15s",
							"&:hover": {
								backgroundColor: "#EEF4FB",
								color: "#1565C0",
							},
							"&:focus-visible": {
								outline: "2px solid #1976D2",
								outlineOffset: "-2px",
								backgroundColor: "#EEF4FB",
							},
						},
						"& .Mui-selected": {
							fontWeight: 700,
							color: "#1D54A2 !important",
							backgroundColor: "#fff",
						},
						"& .MuiTabs-indicator": {
							backgroundColor: "#1D54A2",
							height: 2,
						},
					}}
				>
					{TABS.map((t) => (
						<Tab key={t.value} label={t.label} disableRipple={false} />
					))}
				</Tabs>
			</Box>

			{/* Report table */}
			<Box
				sx={{
					mx: 3,
					mt: 0,
					border: "1.5px solid #E8EDF2",
					borderTop: "none",
					borderRadius: "0 0 8px 8px",
					overflow: "hidden",
					background: "#fff",
				}}
			>
				{tab === 0 && <OverrideReport data={overrideAgents} loading={loading} />}
				{tab === 1 && <AgentPerformanceReport data={performanceRows} loading={loading} />}
				{tab === 2 && <LeadershipReport data={leadershipRows} loading={loading} />}
				{tab === 3 && <MonthlyTotalsReport data={monthlyTotalsRows} loading={loading} />}
			</Box>

			{/* Report footnote */}
			<Stack direction="row" justifyContent="space-between" px={3} mt={2}>
				<Typography fontSize={10} color="text.disabled">
					Override rates: L1 $0.50 · L2 $0.30 · L3 $0.20 per active member
				</Typography>
				<Typography fontSize={10} color="text.disabled">
					Generated: {reports?.generated_at ?? "-"}
				</Typography>
			</Stack>

			{/* Which report tables land in the workbook */}
			<Dialog open={exportOpen} onClose={() => setExportOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#1D54A2", pb: 0.5 }}>Download Reports</DialogTitle>
				<DialogContent>
					<Typography fontSize={12} color="text.secondary" mb={1.5}>
						Choose which tables to include. Each becomes its own sheet in the workbook.
					</Typography>

					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={allSheetsSelected}
								indeterminate={selectedSheets.length > 0 && !allSheetsSelected}
								onChange={toggleAllSheets}
							/>
						}
						label={<Typography fontSize={13} fontWeight={700}>Select all</Typography>}
					/>
					<Divider sx={{ my: 0.5 }} />

					<FormGroup>
						{EXPORT_SHEETS.map((sheet) => (
							<FormControlLabel
								key={sheet.key}
								control={<Checkbox size="small" checked={selectedSheets.includes(sheet.key)} onChange={() => toggleSheet(sheet.key)} />}
								label={<Typography fontSize={13}>{sheet.label}</Typography>}
							/>
						))}
					</FormGroup>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setExportOpen(false)} sx={{ fontSize: 13, color: "text.secondary" }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleExport}
						disabled={selectedSheets.length === 0 || exporting}
						startIcon={exporting ? null : <SystemUpdateAltIcon />}
						sx={{ fontSize: 13, backgroundColor: "#1D54A2 !important" }}
					>
						{exporting ? "Preparing…" : `Download (${selectedSheets.length})`}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
